<script setup lang="ts">
import Dialog from 'primevue/dialog';
import { AppTypography } from '~/components/base';
import type { Candidate } from '~/types/Candidates';

interface Props {
  candidates: Candidate[];
  chamberLabel: string;
}

defineProps<Props>();
const visible = defineModel<boolean>('visible', { default: false });
</script>

<template>
  <Dialog
    v-model:visible="visible"
    :draggable="false"
    modal
    position="top"
    :header="chamberLabel"
    class="app-modal candidates-modal w-full sm:max-w-2xl"
    :dismissable-mask="true"
  >
    <div class="candidates-modal__content flex flex-col gap-4">
      <!-- Candidates list -->
      <ul class="candidates-modal__list flex max-h-96 flex-col overflow-y-auto">
        <NuxtLink
          v-for="candidate in candidates"
          :key="candidate.id"
          :to="`/candidates/${candidate.id}`"
          data-testid="modal-candidate-row"
          class="candidates-modal__row flex items-center gap-4 no-underline"
          @click="visible = false"
        >
          <span
            class="candidates-modal__avatar flex shrink-0 items-center justify-center overflow-hidden"
          >
            <img
              v-if="candidate.image"
              :src="candidate.image"
              :alt="candidate.name"
              width="40"
              height="40"
              loading="lazy"
              decoding="async"
              class="candidates-modal__avatar-image size-full rounded-full object-cover"
            />
            <AppTypography v-else type="body-bold" element="span">
              {{ candidate.initials }}
            </AppTypography>
          </span>

          <div class="flex min-w-0 flex-1 items-center gap-2">
            <AppTypography type="body-bold" element="p" class="truncate">
              {{ candidate.name }}
            </AppTypography>
          </div>

          <span
            v-if="candidate.incumbent"
            class="candidates-modal__chip inline-flex w-fit shrink-0 items-center"
          >
            <AppTypography type="caption-bold" element="span" class="uppercase">
              Incumbent
            </AppTypography>
          </span>

          <Icon
            name="solar:alt-arrow-right-linear"
            class="candidates-modal__chevron shrink-0"
          />
        </NuxtLink>

        <li v-if="candidates.length === 0" class="py-6 text-center">
          <AppTypography type="body" class="text-color-tertiary">
            No candidates found
          </AppTypography>
        </li>
      </ul>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
.candidates-modal {
  &__content {
    min-height: 400px;

    // On the mobile full-screen sheet the wrapper has to fill the content
    // region so the list below can take the leftover height instead of
    // stopping at `max-h-96` and leaving dead space at the bottom.
    @media (max-width: 639.98px) {
      height: 100%;
    }
  }

  // Matches `party-page__cand-list` (app/pages/parties/[id].vue): a white
  // rounded card sitting on top of the modal's sand panel, not bare rows
  // directly on that background.
  &__list {
    padding: 0 $spacing-lg;
    border-radius: $radius-lg;
    background-color: $surface-color-bg;

    @media (max-width: 639.98px) {
      flex: 1 1 auto;
      min-height: 0;
      max-height: none;
    }

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: $text-color-secondary;
      border-radius: 3px;

      &:hover {
        background: $text-color-secondary;
      }
    }
  }

  // Matches `party-page__cand-row` exactly — same padding, divider, hover
  // and last-child treatment as the party page's own primary candidate list
  // rendering this same data.
  &__row {
    padding: $spacing-md 0;
    border: none;
    border-radius: 0;
    border-bottom: 1px solid $complementary-color-shade-2;
    color: $text-color-primary;
    transition: all 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background-color: $surface-color-bg-2;
    }

    &.router-link-active {
      background-color: transparent;
    }
  }

  &__chevron {
    color: $text-color-subtext;
  }

  // Matches `party-page__cand-avatar` — 44px photo circle.
  &__avatar {
    width: 44px;
    height: 44px;
    overflow: hidden;
    border: 2px solid $complementary-color;
    border-radius: 9999px;
    background-color: $surface-color-bg;
    color: $text-color-primary;
    font-weight: 700;
    font-size: 0.875rem;

    &-image {
      border-radius: 9999px;
    }
  }

  // Matches `party-page__cand-chip`.
  &__chip {
    padding: 2px $spacing-sm;
    border-radius: $radius-2xl;
    background-color: $complementary-color-shade-2;
    color: $text-color-subtext;
    font-weight: 600;
    font-size: 0.75rem;
  }
}
</style>

<!--
  Unscoped: themes the teleported PrimeVue dialog chrome via the
  `.candidates-modal` class it carries on the .p-dialog panel. Kept narrow to
  this modal so it doesn't leak to other dialogs.
-->
<style lang="scss">
.candidates-modal.p-dialog {
  // Warm sand sheet to match Figma (replaces the default white dialog) —
  // same literal value as LocationPickerModal.vue's `.p-dialog` override;
  // the list card inside (`.candidates-modal__list`, scoped block above)
  // stays white so it visibly sits on top of this panel.
  background-color: #ece3d1;

  // Figma's "Title 3" style for this modal's header is bold with a 24px
  // line-height; PrimeVue's dialog theme token renders `.p-dialog-title` as
  // semibold/20px/30px-leading by default. Same ambiguity already solved in
  // LocationPickerModal.vue — override locally rather than touch the shared
  // `font-title-3` mixin (that token means something else: the page-level
  // 48/48 H1 on contact.vue/coming-soon.vue).
  .p-dialog-title {
    font-weight: 700;
    line-height: 24px;
  }

  // Centered title with a circular close button pinned to the right —
  // same centering approach as CandidatePartyModal.vue's header, but this
  // modal's close button keeps a filled circle (not a transparent one) per
  // the Figma reference: a light-grey disc with a warm-grey X.
  .p-dialog-header {
    position: relative;
    justify-content: center;
  }

  .p-dialog-title {
    text-align: center;
  }

  .p-dialog-close-button {
    position: absolute;
    top: 50%;
    // Aligns the button's right edge with `.candidates-modal__list`'s right
    // edge (the white card). The header and content regions share the same
    // 24px inset from the panel edge (PrimeVue's `overlay.modal.padding`
    // token, applied to both `.p-dialog-header` and `.p-dialog-content`), and
    // the list card has no margin of its own — so matching that same 24px
    // here lines the two edges up exactly. Confirmed live via
    // `getBoundingClientRect()`: both right edges land at the same x, on both
    // the desktop circle and the mobile bare-X treatments below.
    //
    transform: translateY(-50%);
    border: none;
    box-shadow: none;

    // Bare X, no background, coloured to match every other modal's mobile
    // close button ($text-color-subtext, Figma's text/primary on this sand
    // sheet). Alignment follows the base `_dialog.scss` inset
    // ($spacing-sm), same as the other filter/picker sheets.
    @media (max-width: 639.98px) {
      color: $text-color-subtext;
    }

    // `!important` is required: the shared `.app-modal.app-modal.p-dialog
    // .p-dialog-header .p-dialog-close-button` rule in `_dialog.scss` sets
    // `right: $spacing-sm` at 5 class-selectors of specificity, which beats
    // this rule's 3 (`.candidates-modal.p-dialog .p-dialog-close-button`)
    // regardless of source order.
    //
    // Figma's desktop reference shows a filled light-grey disc behind the X;
    // the mobile full-screen reference shows a bare X with no circle at all —
    // so the circle's box sizing/background is desktop-only. Breakpoint
    // matches this file's own mobile query (`@media (max-width: 639.98px)`,
    // scoped block above) at its inverse.
    @media (min-width: 640px) {
      right: $spacing-xl !important;
      width: 28px;
      height: 28px;
      // PrimeVue's `icon-only` button variant only zeroes *horizontal*
      // padding (`padding-inline: 0`); the base `.p-button` rule's vertical
      // padding (16px top + bottom = 32px) is left standing. With
      // `box-sizing: border-box` a box can't have negative content height,
      // so once that 32px padding sum exceeds this 28px `height`, the
      // browser clamps the rendered box up to 32px tall to fit it — a
      // 28×32 oval instead of a 28×28 circle. Zeroing padding here removes
      // that floor.
      padding: 0;
      color: $border-color-tag;
      background-color: $surface-color-bg-2;

      &:hover,
      &:enabled:hover {
        background-color: $surface-color-bg-2;
      }
    }

    .p-icon {
      width: 14px;
      height: 14px;
    }
  }
}
</style>
